// src/pages/Reservations.jsx (or ReservationsPage.jsx)
import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Calendar,
  Users,
  Clock,
  MapPin,
  Check,
  ArrowRight,
  Ticket,
  Armchair,
  Sun,
  Utensils,
  Share2,
  Copy,
  Phone,
} from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";

/* =========================
   CONFIG
========================= */

const SEATING_ZONES = [
  {
    id: "terrace",
    name: "The Grandstand",
    type: "Sea View Terrace",
    icon: Sun,
    desc: "Open air, sunset views, sea breeze. The classic D10 experience.",
    capacity: "2-8 people",
  },
  {
    id: "indoor",
    name: "The Locker Room",
    type: "Interior & Bar",
    icon: Armchair,
    desc: "AC, music, close to the oven action. High energy atmosphere.",
    capacity: "2-12 people",
  },
];

const TIME_SLOTS = [
  "18:00",
  "18:30",
  "19:00",
  "19:30",
  "20:00",
  "20:30",
  "21:00",
  "21:30",
  "22:00",
];

const WEBHOOK_URL = "https://hook.eu2.make.com/o6vo9qowjwmrxyg4txpx6w4coi2468ho";

// Optional: used in polite error message CTA
const CONTACT_PHONE_DISPLAY = "+385 95 501 1065";
const CONTACT_PHONE_E164 = "+385955011065";

/* =========================
   UI COMPONENTS
========================= */

const LiveTicket = ({ data }) => {
  const zoneName = data.zone
    ? SEATING_ZONES.find((z) => z.id === data.zone)?.name
    : "Any";

  return (
    <div className="relative w-full max-w-sm mx-auto perspective-1000">
      <motion.div
        initial={{ rotateX: 20, opacity: 0 }}
        animate={{ rotateX: 0, opacity: 1 }}
        transition={{ type: "spring", stiffness: 100 }}
        className="bg-white rounded-3xl overflow-hidden shadow-2xl border-2 border-gray-100 relative"
      >
        {/* Ticket Header */}
        <div className="bg-[#0087E6] p-6 text-white relative overflow-hidden">
          <div className="absolute top-0 right-0 p-4 opacity-20">
            <Ticket size={80} />
          </div>
          <div className="font-anton text-3xl uppercase tracking-wider relative z-10">
            Match Day
          </div>
          <div className="text-xs font-manrope font-bold uppercase tracking-[0.2em] opacity-80">
            Table Reservation
          </div>
        </div>

        {/* Ticket Body */}
        <div className="p-6 space-y-6 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')]">
          {/* Row 1: Date & Time */}
          <div className="flex justify-between border-b border-dashed border-gray-200 pb-4">
            <div>
              <span className="text-[10px] font-bold uppercase text-gray-400 tracking-widest block mb-1">
                Kick-off
              </span>
              <div
                className={`font-anton text-2xl ${
                  data.time ? "text-[#0F172A]" : "text-gray-300"
                }`}
              >
                {data.time || "--:--"}
              </div>
            </div>
            <div className="text-right">
              <span className="text-[10px] font-bold uppercase text-gray-400 tracking-widest block mb-1">
                Date
              </span>
              <div
                className={`font-anton text-2xl ${
                  data.date ? "text-[#0F172A]" : "text-gray-300"
                }`}
              >
                {data.date
                  ? new Date(data.date).toLocaleDateString("en-GB", {
                      day: "2-digit",
                      month: "short",
                    })
                  : "--/--"}
              </div>
            </div>
          </div>

          {/* Row 2: Squad & Zone */}
          <div className="flex justify-between border-b border-dashed border-gray-200 pb-4">
            <div>
              <span className="text-[10px] font-bold uppercase text-gray-400 tracking-widest block mb-1">
                Squad
              </span>
              <div className="flex items-center gap-2">
                <Users size={16} className="text-[#0087E6]" />
                <span className="font-bold text-lg">{data.guests}</span>
              </div>
            </div>
            <div className="text-right">
              <span className="text-[10px] font-bold uppercase text-gray-400 tracking-widest block mb-1">
                Zone
              </span>
              <div className="flex items-center gap-2 justify-end">
                <span className="font-bold text-lg capitalize">{zoneName}</span>
                <MapPin size={16} className="text-[#0087E6]" />
              </div>
            </div>
          </div>

          {/* Guest Name */}
          <div>
            <span className="text-[10px] font-bold uppercase text-gray-400 tracking-widest block mb-1">
              Reserved For
            </span>
            <div
              className={`font-manrope font-bold text-xl truncate ${
                data.name ? "text-[#0F172A]" : "text-gray-300"
              }`}
            >
              {data.name || "Guest Name"}
            </div>
          </div>

          {/* Barcode Decoration */}
          <div className="pt-2 opacity-50">
            <div className="h-8 w-full bg-[repeating-linear-gradient(90deg,black,black_2px,transparent_2px,transparent_4px)]" />
            <div className="text-center text-[10px] mt-1 font-mono">
              D10-TICKET-ACCESS-TOKEN
            </div>
          </div>
        </div>

        {/* Cutout Circles */}
        <div className="absolute top-[130px] -left-3 w-6 h-6 bg-[#F8FAFC] rounded-full" />
        <div className="absolute top-[130px] -right-3 w-6 h-6 bg-[#F8FAFC] rounded-full" />
      </motion.div>
    </div>
  );
};

function SuccessScreen({ data, onNew, onCopy, copied, onShare }) {
  const zoneName = SEATING_ZONES.find((z) => z.id === data.zone)?.name || "Any";
  const prettyDate = data.date
    ? new Date(data.date).toLocaleDateString("en-GB", {
        weekday: "short",
        day: "2-digit",
        month: "short",
      })
    : "";

  return (
    <motion.div
      key="success"
      initial={{ opacity: 0, y: 14 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -14 }}
      className="space-y-6"
    >
      <div className="text-center">
        <div className="mx-auto w-14 h-14 rounded-full bg-[#0087E6]/10 flex items-center justify-center mb-4">
          <Check className="text-[#0087E6]" size={28} />
        </div>
        <h2 className="font-anton text-3xl sm:text-4xl uppercase text-[#0F172A]">
          Booking received
        </h2>
        <p className="text-gray-500 mt-2">
          Thanks{" "}
          {data.name ? (
            <span className="font-bold text-gray-700">{data.name}.</span>
          ) : (
            "!"
          )}{" "}
          We’ll confirm shortly by email or phone.
        </p>
      </div>

      <div className="bg-[#F8FAFC] border border-gray-100 rounded-2xl p-5 space-y-3">
        <div className="flex items-center justify-between text-sm">
          <span className="text-gray-500 font-bold uppercase tracking-widest text-[10px]">
            When
          </span>
          <span className="font-bold text-[#0F172A]">
            {prettyDate} · {data.time}
          </span>
        </div>
        <div className="flex items-center justify-between text-sm">
          <span className="text-gray-500 font-bold uppercase tracking-widest text-[10px]">
            Party
          </span>
          <span className="font-bold text-[#0F172A]">{data.guests} guests</span>
        </div>
        <div className="flex items-center justify-between text-sm">
          <span className="text-gray-500 font-bold uppercase tracking-widest text-[10px]">
            Zone
          </span>
          <span className="font-bold text-[#0F172A]">{zoneName}</span>
        </div>
        {data.notes && (
          <div className="pt-3 border-t border-gray-200/70">
            <div className="text-gray-500 font-bold uppercase tracking-widest text-[10px] mb-1">
              Notes
            </div>
            <div className="text-gray-700 text-sm">{data.notes}</div>
          </div>
        )}
      </div>

      <div className="grid sm:grid-cols-2 gap-3">
        <button
          type="button"
          onClick={onShare}
          className="w-full py-3 rounded-xl bg-white border-2 border-gray-100 hover:border-[#0087E6] transition-colors font-bold flex items-center justify-center gap-2"
        >
          <Share2 size={16} className="text-[#0087E6]" />
          Share
        </button>

        <button
          type="button"
          onClick={onCopy}
          className="w-full py-3 rounded-xl bg-white border-2 border-gray-100 hover:border-[#0087E6] transition-colors font-bold flex items-center justify-center gap-2"
        >
          <Copy size={16} className="text-[#0087E6]" />
          {copied ? "Copied ✓" : "Copy details"}
        </button>
      </div>

      <div className="pt-2">
        <button
          type="button"
          onClick={onNew}
          className="w-full py-4 bg-[#0F172A] text-white font-anton uppercase text-lg rounded-xl hover:bg-[#0087E6] transition-colors shadow-xl"
        >
          Make another reservation
        </button>
      </div>

      <p className="text-xs text-gray-400 text-center">
        Note: This is a request. We’ll confirm availability as soon as possible.
      </p>
    </motion.div>
  );
}

/* =========================
   PAGE
========================= */

export default function ReservationsPage() {
  const [step, setStep] = useState(1);
  const [submitted, setSubmitted] = useState(false);
  const [copied, setCopied] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // "idle" | "error"
  const [submitError, setSubmitError] = useState("idle");

  const INITIAL_FORM = {
    date: "",
    time: "",
    guests: 2,
    zone: "terrace",
    name: "",
    email: "",
    phone: "",
    notes: "",
  };

  const [formData, setFormData] = useState(INITIAL_FORM);

  // Scroll to top on step change or submit
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [step, submitted]);

  const updateForm = (field, value) =>
    setFormData((prev) => ({ ...prev, [field]: value }));

  const handleNext = (e) => {
    e.preventDefault();
    setStep((prev) => Math.min(3, prev + 1));
  };

  const buildReservationSummary = () => {
    const zoneName =
      SEATING_ZONES.find((z) => z.id === formData.zone)?.name || "Any";
    const datePretty = formData.date
      ? new Date(formData.date).toLocaleDateString("en-GB", {
          weekday: "short",
          day: "2-digit",
          month: "short",
          year: "numeric",
        })
      : "";
    return [
      "D10 Reservation",
      `Name: ${formData.name}`,
      `When: ${datePretty} at ${formData.time}`,
      `Guests: ${formData.guests}`,
      `Zone: ${zoneName}`,
      formData.phone ? `Phone: ${formData.phone}` : "",
      formData.email ? `Email: ${formData.email}` : "",
      formData.notes ? `Notes: ${formData.notes}` : "",
    ]
      .filter(Boolean)
      .join("\n");
  };

  const copyDetails = async () => {
    const text = buildReservationSummary();
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1400);
    } catch {
      // fallback
      const ta = document.createElement("textarea");
      ta.value = text;
      ta.setAttribute("readonly", "");
      ta.style.position = "absolute";
      ta.style.left = "-9999px";
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
      setCopied(true);
      setTimeout(() => setCopied(false), 1400);
    }
  };

  const shareDetails = async () => {
    const text = buildReservationSummary();
    if (navigator?.share) {
      try {
        await navigator.share({
          title: "D10 Reservation",
          text,
          url: window.location.href,
        });
        return;
      } catch {
        // user canceled -> ignore
      }
    }
    await copyDetails();
  };

  const isStep1Valid = Boolean(formData.date && formData.time);
  const isStep3Valid =
    formData.name.trim() &&
    formData.phone.trim() &&
    formData.email.trim();

  const handleSubmitFinal = async (e) => {
    e.preventDefault();
    if (!isStep3Valid) return;

    setIsSubmitting(true);
    setSubmitError("idle");

    const zoneName =
      SEATING_ZONES.find((z) => z.id === formData.zone)?.name || "Any";

    const payload = {
      source: "d10-website",
      form: "reservation",
      page: "/reservations",
      submittedAt: new Date().toISOString(),
      reservation: {
        date: formData.date,
        time: formData.time,
        guests: Number(formData.guests),
        zoneId: formData.zone,
        zoneName,
        name: formData.name.trim(),
        email: formData.email.trim(),
        phone: formData.phone.trim(),
        notes: formData.notes?.trim() || "",
      },
      meta: {
        userAgent: navigator?.userAgent || "",
        language: navigator?.language || "",
        timezone: Intl?.DateTimeFormat?.().resolvedOptions?.().timeZone || "",
      },
    };

    try {
      const res = await fetch(WEBHOOK_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!res.ok) throw new Error(`Webhook failed: ${res.status}`);

      setSubmitted(true);
    } catch {
      // ✅ no raw errors for the user
      setSubmitError("error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetAll = () => {
    setSubmitted(false);
    setStep(1);
    setFormData(INITIAL_FORM);
    setCopied(false);
    setIsSubmitting(false);
    setSubmitError("idle");
  };

  return (
    <div className="bg-[#F8FAFC] min-h-screen selection:bg-[#0087E6] selection:text-white">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Anton&family=Manrope:wght@400;500;700;800&family=Playfair+Display:ital,wght@0,400;1,400&display=swap');
        .font-anton { font-family: 'Anton', sans-serif; }
        .font-manrope { font-family: 'Manrope', sans-serif; }
        .font-serif { font-family: 'Playfair Display', serif; }
      `}</style>

      <Navbar />

      <main className="pt-24 pb-20 px-4">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <h1 className="font-anton text-5xl md:text-7xl text-[#0F172A] uppercase mb-4">
              Secure Your <span className="text-[#0087E6]">Seat</span>
            </h1>
            <p className="font-serif italic text-gray-500 text-lg md:text-xl">
              "The pizza is hot, the drinks are cold, and the view is ready."
            </p>
          </div>

          <div className="grid lg:grid-cols-12 gap-12 items-start">
            {/* LEFT COLUMN */}
            <div className="lg:col-span-7">
              <div className="bg-white rounded-3xl shadow-xl p-8 md:p-10 border border-gray-100 relative overflow-hidden">
                {/* Progress */}
                {!submitted && (
                  <div className="flex gap-2 mb-8">
                    {[1, 2, 3].map((i) => (
                      <div
                        key={i}
                        className={`h-1.5 flex-1 rounded-full transition-colors ${
                          step >= i ? "bg-[#0087E6]" : "bg-gray-100"
                        }`}
                      />
                    ))}
                  </div>
                )}

                <AnimatePresence mode="wait">
                  {submitted ? (
                    <SuccessScreen
                      data={formData}
                      onNew={resetAll}
                      onCopy={copyDetails}
                      copied={copied}
                      onShare={shareDetails}
                    />
                  ) : (
                    <motion.form
                      key="form"
                      onSubmit={step === 3 ? handleSubmitFinal : handleNext}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                    >
                      <AnimatePresence mode="wait">
                        {/* STEP 1 */}
                        {step === 1 && (
                          <motion.div
                            key="step1"
                            initial={{ opacity: 0, x: 20 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: -20 }}
                            className="space-y-8"
                          >
                            <h3 className="font-anton text-2xl uppercase">
                              Match Details
                            </h3>

                            {/* Guests */}
                            <div>
                              <label className="text-xs font-bold uppercase tracking-widest text-gray-400 mb-4 block">
                                Squad Size
                              </label>
                              <div className="flex items-center gap-6">
                                <button
                                  type="button"
                                  onClick={() =>
                                    updateForm(
                                      "guests",
                                      Math.max(1, formData.guests - 1)
                                    )
                                  }
                                  className="w-12 h-12 rounded-full border-2 border-gray-200 flex items-center justify-center hover:border-[#0087E6] transition-colors font-bold text-xl"
                                >
                                  -
                                </button>
                                <span className="font-anton text-4xl w-12 text-center">
                                  {formData.guests}
                                </span>
                                <button
                                  type="button"
                                  onClick={() =>
                                    updateForm(
                                      "guests",
                                      Math.min(12, formData.guests + 1)
                                    )
                                  }
                                  className="w-12 h-12 rounded-full border-2 border-gray-200 flex items-center justify-center hover:border-[#0087E6] transition-colors font-bold text-xl"
                                >
                                  +
                                </button>
                              </div>
                              {formData.guests > 8 && (
                                <p className="text-xs text-[#0087E6] mt-2 font-bold flex items-center gap-1">
                                  <Users size={12} /> Large group? We’ll confirm
                                  via phone.
                                </p>
                              )}
                            </div>

                            {/* Date */}
                            <div>
                              <label className="text-xs font-bold uppercase tracking-widest text-gray-400 mb-2 block">
                                Match Day
                              </label>
                              <div className="relative">
                                <input
                                  type="date"
                                  required
                                  min={new Date().toISOString().split("T")[0]}
                                  className="w-full bg-[#F8FAFC] border-2 border-gray-100 rounded-xl px-4 py-4 font-manrope font-bold text-lg outline-none focus:border-[#0087E6] transition-colors appearance-none"
                                  onChange={(e) =>
                                    updateForm("date", e.target.value)
                                  }
                                  value={formData.date}
                                />
                                <Calendar className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                              </div>
                            </div>

                            {/* Time */}
                            <div>
                              <label className="text-xs font-bold uppercase tracking-widest text-gray-400 mb-4 block">
                                Kick-off Time
                              </label>
                              <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
                                {TIME_SLOTS.map((t) => (
                                  <button
                                    key={t}
                                    type="button"
                                    onClick={() => updateForm("time", t)}
                                    className={`py-3 rounded-lg text-sm font-bold transition-all ${
                                      formData.time === t
                                        ? "bg-[#0F172A] text-white shadow-lg scale-105"
                                        : "bg-[#F8FAFC] text-gray-600 hover:bg-blue-50"
                                    }`}
                                  >
                                    {t}
                                  </button>
                                ))}
                              </div>
                            </div>

                            <div className="pt-4">
                              <button
                                type="submit"
                                disabled={!isStep1Valid}
                                className="w-full py-4 bg-[#0087E6] text-white font-anton uppercase text-lg rounded-xl hover:bg-[#0F172A] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                              >
                                Select Zone <ArrowRight size={20} />
                              </button>
                            </div>
                          </motion.div>
                        )}

                        {/* STEP 2 */}
                        {step === 2 && (
                          <motion.div
                            key="step2"
                            initial={{ opacity: 0, x: 20 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: -20 }}
                            className="space-y-8"
                          >
                            <div className="flex items-center justify-between">
                              <h3 className="font-anton text-2xl uppercase">
                                Choose Your View
                              </h3>
                              <button
                                type="button"
                                onClick={() => setStep(1)}
                                className="text-sm font-bold text-gray-400 underline"
                              >
                                Change Details
                              </button>
                            </div>

                            <div className="grid gap-4">
                              {SEATING_ZONES.map((zone) => (
                                <button
                                  key={zone.id}
                                  type="button"
                                  onClick={() => updateForm("zone", zone.id)}
                                  className={`relative p-6 rounded-2xl border-2 text-left transition-all group ${
                                    formData.zone === zone.id
                                      ? "border-[#0087E6] bg-blue-50/50"
                                      : "border-gray-100 bg-white hover:border-blue-200"
                                  }`}
                                >
                                  <div className="flex items-start gap-4">
                                    <div
                                      className={`p-3 rounded-full ${
                                        formData.zone === zone.id
                                          ? "bg-[#0087E6] text-white"
                                          : "bg-gray-100 text-gray-500"
                                      }`}
                                    >
                                      <zone.icon size={24} />
                                    </div>
                                    <div>
                                      <h4 className="font-anton text-xl text-[#0F172A] uppercase">
                                        {zone.name}
                                      </h4>
                                      <span className="text-xs font-bold text-[#0087E6] uppercase tracking-widest mb-2 block">
                                        {zone.type}
                                      </span>
                                      <p className="text-sm text-gray-500 leading-snug">
                                        {zone.desc}
                                      </p>
                                      <p className="text-[11px] text-gray-400 mt-2 font-bold uppercase tracking-widest">
                                        {zone.capacity}
                                      </p>
                                    </div>
                                  </div>
                                  {formData.zone === zone.id && (
                                    <div className="absolute top-4 right-4 text-[#0087E6]">
                                      <Check size={24} />
                                    </div>
                                  )}
                                </button>
                              ))}
                            </div>

                            <div className="pt-4">
                              <button
                                type="submit"
                                className="w-full py-4 bg-[#0087E6] text-white font-anton uppercase text-lg rounded-xl hover:bg-[#0F172A] transition-colors flex items-center justify-center gap-2"
                              >
                                Final Details <ArrowRight size={20} />
                              </button>
                            </div>
                          </motion.div>
                        )}

                        {/* STEP 3 */}
                        {step === 3 && (
                          <motion.div
                            key="step3"
                            initial={{ opacity: 0, x: 20 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: -20 }}
                            className="space-y-6"
                          >
                            <div className="flex items-center justify-between mb-4">
                              <h3 className="font-anton text-2xl uppercase">
                                Contact Info
                              </h3>
                              <button
                                type="button"
                                onClick={() => setStep(2)}
                                className="text-sm font-bold text-gray-400 underline"
                              >
                                Back
                              </button>
                            </div>

                            {/* ✅ Friendly error only (no raw error/status codes) */}
                            {submitError === "error" && (
                              <div className="rounded-2xl border border-red-200 bg-red-50 p-4">
                                <p className="text-sm font-manrope text-red-700">
                                  Couldn’t send your request right now. Please
                                  try again — or call us at{" "}
                                  <a
                                    className="font-bold underline"
                                    href={`tel:${CONTACT_PHONE_E164}`}
                                  >
                                    {CONTACT_PHONE_DISPLAY}
                                  </a>
                                  .
                                </p>
                              </div>
                            )}

                            <div className="grid md:grid-cols-2 gap-4">
                              <input
                                type="text"
                                placeholder="Full Name"
                                required
                                value={formData.name}
                                onChange={(e) => updateForm("name", e.target.value)}
                                className="w-full bg-[#F8FAFC] border-2 border-gray-100 rounded-xl px-4 py-3 font-manrope outline-none focus:border-[#0087E6]"
                              />
                              <input
                                type="tel"
                                placeholder="Phone Number"
                                required
                                value={formData.phone}
                                onChange={(e) => updateForm("phone", e.target.value)}
                                className="w-full bg-[#F8FAFC] border-2 border-gray-100 rounded-xl px-4 py-3 font-manrope outline-none focus:border-[#0087E6]"
                              />
                            </div>

                            <input
                              type="email"
                              placeholder="Email Address"
                              required
                              value={formData.email}
                              onChange={(e) => updateForm("email", e.target.value)}
                              className="w-full bg-[#F8FAFC] border-2 border-gray-100 rounded-xl px-4 py-3 font-manrope outline-none focus:border-[#0087E6]"
                            />

                            <textarea
                              placeholder="Special Requests (High chair, Allergies, Special Occasion...)"
                              rows={3}
                              value={formData.notes}
                              onChange={(e) => updateForm("notes", e.target.value)}
                              className="w-full bg-[#F8FAFC] border-2 border-gray-100 rounded-xl px-4 py-3 font-manrope outline-none focus:border-[#0087E6] resize-none"
                            />

                            <div className="bg-blue-50 p-4 rounded-xl flex gap-3 text-sm text-blue-800">
                              <Clock className="flex-shrink-0" size={20} />
                              <p>
                                Please note: Tables are held for 15 minutes past
                                reservation time. Standard seating time is 1.5
                                hours.
                              </p>
                            </div>

                            <div className="pt-2">
                              <button
                                type="submit"
                                disabled={isSubmitting || !isStep3Valid}
                                className="w-full py-4 bg-[#0F172A] text-white font-anton uppercase text-xl rounded-xl hover:bg-[#0087E6] transition-colors shadow-xl disabled:opacity-60 disabled:cursor-not-allowed"
                              >
                                {isSubmitting ? "Sending..." : "Confirm Booking"}
                              </button>
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </motion.form>
                  )}
                </AnimatePresence>
              </div>
            </div>

            {/* RIGHT COLUMN: TICKET */}
            <div className="lg:col-span-5 lg:sticky lg:top-24">
              <div className="mb-6 text-center lg:text-left">
                <span className="text-[#0087E6] font-bold uppercase tracking-widest text-xs">
                  Live Preview
                </span>
                <h3 className="font-anton text-2xl uppercase">Your Access Pass</h3>
              </div>

              <LiveTicket data={formData} />

              <div className="mt-8 grid grid-cols-2 gap-4 text-center">
                <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100">
                  <Sun className="mx-auto text-[#0087E6] mb-2" size={20} />
                  <div className="font-bold text-xs uppercase tracking-wide">
                    Weather Check
                  </div>
                  <div className="text-[10px] text-gray-500">
                    Covered terrace available
                  </div>
                </div>
                <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100">
                  <Utensils className="mx-auto text-[#0087E6] mb-2" size={20} />
                  <div className="font-bold text-xs uppercase tracking-wide">
                    Dietary
                  </div>
                  <div className="text-[10px] text-gray-500">
                    GF & Vegan options
                  </div>
                </div>
              </div>

              <div className="mt-4 bg-white p-4 rounded-xl shadow-sm border border-gray-100 flex items-center justify-between">
                <div>
                  <div className="text-[10px] font-bold uppercase tracking-widest text-gray-400">
                    Need it faster?
                  </div>
                  <div className="font-bold text-[#0F172A]">
                    Call the squad
                  </div>
                </div>
                <a
                  href={`tel:${CONTACT_PHONE_E164}`}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#0087E6] text-white font-bold text-xs uppercase tracking-widest hover:bg-[#0F172A] transition-colors"
                >
                  <Phone size={16} />
                  Call
                </a>
              </div>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
